import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { sortVariants, formatVariantLabel } from "@/lib/variants";
import { aggregateForProduct, groupByWarehouse, aggregateForVariant } from "@/lib/stockAvailability";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const num = (v) => Number(v || 0);
const CARD = "rounded-sm border p-3 md:p-4";
const LBL = "text-[10px] uppercase font-medium opacity-80";
const VAL = "text-xl md:text-2xl font-bold mt-1 tabular-nums";

// FAZ 2C — Stok / Rezerve / Yolda / Kullanılabilir paneli.
// Tek veri kaynağı: /inventory/stock-availability response satırları (hesap yok).
export default function StockAvailabilityPanel({ productId, rows, error, variants = [], warehouses = [], summary }) {
  const agg = useMemo(() => (rows ? aggregateForProduct(rows, productId) : null), [rows, productId]);
  const whName = useMemo(() => {
    const m = {};
    for (const w of warehouses || []) m[w.id] = w.name || w.code || w.id;
    return m;
  }, [warehouses]);

  const sortedVs = useMemo(() => sortVariants(variants || []), [variants]);
  const vLabel = useMemo(() => {
    const m = {};
    for (const v of sortedVs) m[v.id] = formatVariantLabel(v.attributes, v.sku || v.id.slice(0, 8));
    return m;
  }, [sortedVs]);

  const whGroups = useMemo(() => {
    const g = groupByWarehouse(agg?.rows || []);
    const depots = Object.values(g)
      .filter((x) => x.warehouse_id !== "")
      .sort((a, b) => (whName[a.warehouse_id] || "").localeCompare(whName[b.warehouse_id] || "", "tr"));
    return { depots, incomingOnly: g[""] || null };
  }, [agg, whName]);

  const hasVariants = sortedVs.length > 0;
  const low = summary?.is_low;

  if (error) {
    return (
      <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2" data-testid="pd-avail-error">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        Stok bilgileri şu an alınamadı. Lütfen daha sonra tekrar deneyin.
      </div>
    );
  }
  if (!agg) return null;

  // Varyant matrisi kolonları: gerçek varyantların bedenleri (sabit S/M/L YOK)
  const sizeCols = [];
  const sizeToVariants = {};
  for (const v of sortedVs) {
    const s = v.attributes?.size || vLabel[v.id];
    if (!sizeToVariants[s]) { sizeToVariants[s] = []; sizeCols.push(s); }
    sizeToVariants[s].push(v.id);
  }
  const cellPhysical = (groupRows, vids) =>
    groupRows.filter((r) => vids.includes(r.variant_id || "")).reduce((s, r) => s + num(r.physical), 0);
  const cellIncoming = (groupRows, vids) =>
    groupRows.filter((r) => vids.includes(r.variant_id || "")).reduce((s, r) => s + num(r.incoming), 0);
  const showIncomingRow = whGroups.incomingOnly && (whGroups.incomingOnly.incoming > 0 || whGroups.incomingOnly.availableIncoming > 0);

  const thCls = "text-[10px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground";
  const tdR = "px-3 py-2 text-right font-mono tabular-nums";

  return (
    <div className="space-y-4" data-testid="pd-avail-panel">
      {/* 4 ana metrik — endpoint toplamları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pd-avail-cards">
        <div className={`${CARD} bg-emerald-500/10 border-emerald-500/40`}>
          <div className={`${LBL} text-emerald-600 dark:text-emerald-400`}>Toplam Stok</div>
          <div className={`${VAL} text-emerald-600 dark:text-emerald-400`} data-testid="pd-avail-total">{fmt(agg.physical)}</div>
        </div>
        <div className={`${CARD} bg-amber-500/10 border-amber-500/40`}>
          <div className={`${LBL} text-amber-600 dark:text-amber-400`}>Rezerve</div>
          <div className={`${VAL} text-amber-600 dark:text-amber-400`} data-testid="pd-avail-reserved">{fmt(agg.reserved)}</div>
        </div>
        <div className={`${CARD} bg-sky-500/10 border-sky-500/40`}>
          <div className={`${LBL} text-sky-600 dark:text-sky-400`}>Yolda</div>
          <div className={`${VAL} text-sky-600 dark:text-sky-400`} data-testid="pd-avail-incoming">{fmt(agg.incoming)}</div>
        </div>
        <div className={`${CARD} ${low ? "bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-400" : "bg-blue-500/10 border-blue-500/40 text-blue-600 dark:text-blue-400"}`}>
          <div className={`${LBL}`}>Kullanılabilir{summary?.critical_stock ? ` · Kritik: ${summary.critical_stock}` : ""}</div>
          <div className={VAL} data-testid="pd-avail-available">
            {fmt(agg.available)}
            {low && <AlertTriangle className="w-4 h-4 inline ml-2" />}
          </div>
        </div>
      </div>

      {/* Depo bazlı stok */}
      <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30">
        <div className="p-3 border-b border-border text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
          Depo Bazlı Stok
        </div>
        {whGroups.depots.length === 0 && !showIncomingRow ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Kayıt yok</div>
        ) : hasVariants ? (
          <div className="overflow-x-auto" data-testid="pd-wh-matrix">
            <table className="w-full text-sm min-w-[480px]">
              <thead className="bg-muted/40 border-b border-border">
                <tr className={thCls}>
                  <th className="text-left px-3 py-2">Depo</th>
                  {sizeCols.map((s) => <th key={s} className="text-right px-3 py-2">{s}</th>)}
                  <th className="text-right px-3 py-2">Toplam</th>
                  <th className="text-right px-3 py-2">Rezerve</th>
                  <th className="text-right px-3 py-2">Kullanılabilir</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {whGroups.depots.map((g) => (
                  <tr key={g.warehouse_id} data-testid={`pd-wh-row-${g.warehouse_id}`}>
                    <td className="px-3 py-2 font-semibold">{whName[g.warehouse_id] || g.warehouse_id}</td>
                    {sizeCols.map((s) => (
                      <td key={s} className={tdR}>{fmt(cellPhysical(g.rows, sizeToVariants[s]))}</td>
                    ))}
                    <td className={`${tdR} font-bold`}>{fmt(g.physical)}</td>
                    <td className={`${tdR} text-amber-500`}>{fmt(g.reserved)}</td>
                    <td className={tdR}>{fmt(g.availablePhysical)}</td>
                  </tr>
                ))}
                {showIncomingRow && (
                  <tr className="bg-sky-500/5" data-testid="pd-wh-row-incoming">
                    <td className="px-3 py-2 font-semibold text-sky-600 dark:text-sky-400">Yolda (depo ataması yok)</td>
                    {sizeCols.map((s) => (
                      <td key={s} className={`${tdR} text-sky-600 dark:text-sky-400`}>{fmt(cellIncoming(whGroups.incomingOnly.rows, sizeToVariants[s]))}</td>
                    ))}
                    <td className={`${tdR} font-bold text-sky-600 dark:text-sky-400`}>{fmt(whGroups.incomingOnly.incoming)}</td>
                    <td className={`${tdR} text-muted-foreground`} title="Rezerve yoldan payı depo satırlarında izlenir">—</td>
                    <td className={`${tdR} text-sky-600 dark:text-sky-400`}>{fmt(whGroups.incomingOnly.availableIncoming)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
              * Yolda miktarları depo ataması olmadan izlenir; bir depoya dağıtılmaz.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="pd-wh-table">
            <table className="w-full text-sm min-w-[420px]">
              <thead className="bg-muted/40 border-b border-border">
                <tr className={thCls}>
                  <th className="text-left px-3 py-2">Depo</th>
                  <th className="text-right px-3 py-2">Fiziksel</th>
                  <th className="text-right px-3 py-2">Rezerve</th>
                  <th className="text-right px-3 py-2">Yolda</th>
                  <th className="text-right px-3 py-2">Kullanılabilir</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {whGroups.depots.map((g) => (
                  <tr key={g.warehouse_id} data-testid={`pd-wh-row-${g.warehouse_id}`}>
                    <td className="px-3 py-2 font-semibold">{whName[g.warehouse_id] || g.warehouse_id}</td>
                    <td className={tdR}>{fmt(g.physical)}</td>
                    <td className={`${tdR} text-amber-500`}>{fmt(g.reserved)}</td>
                    <td className={`${tdR} text-muted-foreground`} title="Depo ataması yok">—</td>
                    <td className={tdR}>{fmt(g.availablePhysical)}</td>
                  </tr>
                ))}
                {showIncomingRow && (
                  <tr className="bg-sky-500/5" data-testid="pd-wh-row-incoming">
                    <td className="px-3 py-2 font-semibold text-sky-600 dark:text-sky-400">Yolda (depo ataması yok)</td>
                    <td className={`${tdR} text-muted-foreground`}>—</td>
                    <td className={`${tdR} text-muted-foreground`} title="Rezerve yoldan payı depo satırlarında izlenir">—</td>
                    <td className={`${tdR} text-sky-600 dark:text-sky-400`}>{fmt(whGroups.incomingOnly.incoming)}</td>
                    <td className={`${tdR} text-sky-600 dark:text-sky-400`}>{fmt(whGroups.incomingOnly.availableIncoming)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Varyant bazlı durum */}
      {hasVariants && (
        <Card className="rounded-sm bg-[#FCFCFD] dark:bg-card border border-slate-300 dark:border-border shadow-[0_1px_2px_rgba(16,24,40,0.05),0_8px_24px_-12px_rgba(16,24,40,0.10)] dark:shadow-md dark:shadow-black/30">
          <div className="p-3 border-b border-border text-[11px] uppercase tracking-wide font-medium text-slate-500 dark:text-muted-foreground">
            Varyant Bazlı Durum
          </div>
          <div className="overflow-x-auto" data-testid="pd-variant-status">
            <table className="w-full text-sm min-w-[420px]">
              <thead className="bg-muted/40 border-b border-border">
                <tr className={thCls}>
                  <th className="text-left px-3 py-2">Varyant</th>
                  <th className="text-right px-3 py-2">Fiziksel</th>
                  <th className="text-right px-3 py-2">Rezerve</th>
                  <th className="text-right px-3 py-2">Yolda *</th>
                  <th className="text-right px-3 py-2">Kullanılabilir</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedVs.map((v) => {
                  const a = aggregateForVariant(agg.rows, v.id);
                  return (
                    <tr key={v.id} data-testid={`pd-var-row-${v.id}`}>
                      <td className="px-3 py-2 font-medium">{vLabel[v.id]}</td>
                      <td className={tdR}>{fmt(a.physical)}</td>
                      <td className={`${tdR} text-amber-500`}>{fmt(a.reserved)}</td>
                      <td className={`${tdR} text-sky-600 dark:text-sky-400`}>{fmt(a.incoming)}</td>
                      <td className={`${tdR} font-semibold`}>{fmt(a.available)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-3 py-2 text-[10px] text-muted-foreground border-t border-border">
              * Yolda miktarı depo ataması olmadan izlenir. Kullanılabilir = fiziksel kullanılabilir + yolda kullanılabilir.
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
